import { css } from '@emotion/react';
import { useContext, useEffect, useMemo } from 'react';
import t from 'react-translate';
import { AngularContext } from 'react-app';
import NvIcon from 'shared/components/nv-icon';
import { PanelStateKey } from 'athena/components/constants';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import CollapsiblePanel from 'athena/components/collapsible-panel';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import {
  MentorshipProgramSession,
  NextStepKey,
  SessionFilters,
} from 'redux/schemas/models/mentoring-program-sessions';
import {
  CompletionStatus,
  MentoringProgramEnrollment,
} from 'redux/schemas/models/mentoring-program-enrollments';
import { gray2 } from 'styles/global_defaults/colors';
import { usePaginatedApi } from 'shared/hooks/use-paginated-api';
import {
  getMentorshipProgramSessionsList,
  getResetMentoringProgramNextStepsState,
} from 'redux/selectors/mentorship-program-sessions';
import { useAppDispatch } from 'redux/store';
import {
  getMentoringProgramSessions,
  resetMentoringProgramNextSteps,
} from 'redux/actions/mentoring-program-sessions';
import { useSelector } from 'react-redux';
import { config } from '@config/pendo.config.json';
import moment from 'moment';
import NvTooltip from 'shared/components/nv-tooltip';
import { getMentoringProgramEnrollment } from 'redux/selectors/mentoring-program-enrollments';
import { selectMentoringProgramReflectionParticipantResponses } from 'redux/selectors/mentoring-program-reflections';
import { Step } from '../step-list/types';
import StepItem from '../step-list/step-item';
import { getConnectionName } from './utils';

const styles = css`
  gap: ${halfSpacing}px;
`;

const ctaStyles = css`
  .cta-button {
    color: ${gray2};
    opacity: 0;
    transition: all 0.3s ease-in-out;
  }
`;

type NextStepsPanelProps = {
  programId: number;
  connectionId: number;
  enrollmentId: number;
  openSessionPulseCheck: (sessionId: number) => void;
  showPlanSessionFlyout: (
    session: any,
    editMode?: boolean,
    createMeetingPlan?: boolean
  ) => void;
};

const NextStepsPanel = ({
  programId,
  connectionId,
  enrollmentId,
  openSessionPulseCheck,
  showPlanSessionFlyout,
}: NextStepsPanelProps) => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.NEXT_STEPS;
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { pulseChecksEnabled, programReflectionEnabled } = mentoringProgram || {};
  const resetNextSteps = useSelector(getResetMentoringProgramNextStepsState);
  const mentorshipProgramEnrollment: MentoringProgramEnrollment = useSelector(
    (state) => getMentoringProgramEnrollment(state, enrollmentId),
  );
  const { states: enrollmentStates, assignedConnections } = mentorshipProgramEnrollment || {};
  const connection = assignedConnections?.find(
    (connec) => connec.connectionId === connectionId,
  ) ?? assignedConnections?.[0];
  const {
    isSessionCreated,
    states: connectionStates,
    pendingReflection = false,
  } = connection || {};
  const states = useMemo(
    () => ({ ...enrollmentStates, ...connectionStates }),
    [enrollmentStates, connectionStates],
  );

  const params = useMemo(
    () => ({
      pageSize: 1,
      filter: SessionFilters.PAST,
      sortByCreated: 'desc',
    }),
    [],
  );

  const { result, reset } = usePaginatedApi<any, any>(
    (p) => dispatch(
      getMentoringProgramSessions({
        programId,
        connectionId,
        ...p,
      }),
    ).then((action) => action.payload),
    params,
    getMentorshipProgramSessionsList,
  );

  const [lastSession] = (result || []) as MentorshipProgramSession[];
  const {
    id,
    title: sessionTitle,
    pulseCheckResponses,
    mentorshipProgramConnection,
    scheduledDate,
    duration,
  } = lastSession || {};

  const connectionName = getConnectionName(
    mentorshipProgramConnection,
    enrollmentId,
  );
  const participantResponses = useSelector((state) => (
    selectMentoringProgramReflectionParticipantResponses(state, connectionId)
  ));
  const hasRepliedReflection = participantResponses?.data?.find(
    (r) => r.mentorshipProgramEnrollmentId === enrollmentId,
  )?.answers?.length > 0;

  const isPastSession = lastSession && moment().isAfter(
    moment(scheduledDate).add(duration || 0, 'minutes'),
  );
  const shouldShowPulseCheckStep = pulseChecksEnabled && isPastSession && !pulseCheckResponses?.[enrollmentId];
  const shouldShowReflectionsStep = programReflectionEnabled
    && pendingReflection
    && participantResponses?.isLoading === false
    && !hasRepliedReflection;
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome;

  const hasMeetingPlan = () => states[NextStepKey.CREATE_MEETING_PLAN]?.status
    === CompletionStatus.COMPLETED;

  const nextStepsList: { [key in NextStepKey]?: Step } = {};

  if (shouldShowPulseCheckStep) {
    nextStepsList[NextStepKey.SESSION_PULSE_CHECK] = {
      type: 'nextStep',
      title: translationBase.SESSION_PULSE_CHECK_STEP.TITLE(connectionName),
      content: translationBase.SESSION_PULSE_CHECK_STEP.SUBTITLE(),
      ctaButton: (
        <NvTooltip
          placement='top'
          text={translationBase.SESSION_PULSE_CHECK_STEP.TOOLTIP()}
        >
          <div css={ctaStyles}>
            <NvIcon className='cta-button' icon='expand' size='small' />
          </div>
        </NvTooltip>
      ),
      onClick: () => openSessionPulseCheck(id),
      ariaLabel:
        translationBase.SESSION_PULSE_CHECK_STEP.ARIA_LABEL.OPEN_SESSION_PULSE_CHECK(
          sessionTitle,
        ),
      dataQa: dataQATag.nextSteps.items.sessionPulseCheck,
      dataQaId: `${dataQATag.nextSteps.items.sessionPulseCheck}_${id}`,
    };
  }

  if (shouldShowReflectionsStep) {
    nextStepsList[NextStepKey.PROGRAM_REFLECTION] = {
      type: 'nextStep',
      title: translationBase.PROGRAM_REFLECTIONS.TITLE(),
      content: translationBase.PROGRAM_REFLECTIONS.CONTENT(connectionName),
      ctaButton: (
        <NvTooltip
          placement='top'
          text={translationBase.PROGRAM_REFLECTIONS.TOOLTIP()}
        >
          <div css={ctaStyles}>
            <NvIcon className='cta-button' icon='expand' size='small' />
          </div>
        </NvTooltip>
      ),
      onClick: () => (
        $state.go($state.current, { showReflections: true }, { notify: false })
      ),
      ariaLabel: translationBase.PROGRAM_REFLECTIONS.ARIA_LABEL(),
      dataQa: dataQATag.nextSteps.items.programReflections,
      dataQaId: `${dataQATag.nextSteps.items.programReflections}_${programId}`,
    };
  }

  if (!hasMeetingPlan()) {
    nextStepsList[NextStepKey.CREATE_MEETING_PLAN] = {
      type: 'nextStep',
      title: translationBase.CREATE_MEETING_PLAN.TITLE(),
      content: translationBase.CREATE_MEETING_PLAN.CONTENT(),
      ctaButton: (
        <NvTooltip
          placement='top'
          text={translationBase.CREATE_MEETING_PLAN.TOOLTIP()}
        >
          <div css={ctaStyles}>
            <NvIcon className='cta-button' icon='expand' size='small' />
          </div>
        </NvTooltip>
      ),
      onClick: () => showPlanSessionFlyout(null, true, true),
      ariaLabel:
        translationBase.SESSION_PULSE_CHECK_STEP.ARIA_LABEL.OPEN_SESSION_PULSE_CHECK(
          sessionTitle,
        ),
      dataQa: dataQATag.nextSteps.items.createMeetingPlan,
      dataQaId: `${dataQATag.nextSteps.items.createMeetingPlan}_${programId}`,
    };
  }

  useEffect(() => {
    if (resetNextSteps) {
      reset();
      dispatch(resetMentoringProgramNextSteps({ resetNextSteps: false }));
    }
  }, [dispatch, reset, resetNextSteps]);

  if (hasMeetingPlan() && !shouldShowPulseCheckStep && !shouldShowReflectionsStep) {
    return null;
  }

  return isSessionCreated ? (
    <CollapsiblePanel
      title={translationBase.TITLE()}
      panelState={{
        persistState: true,
        localStorageKey: PanelStateKey.NEXT_STEPS_STATE,
      }}
      ariaLabel={translationBase.TITLE()}
      dataQa={dataQATag.nextSteps.panel}
      dataQaId={`${dataQATag.nextSteps.panel}-${programId}-${connectionId}-${enrollmentId}-${id}`}
    >
      <div css={styles} className='d-flex flex-column mt-4'>
        {Object.entries(nextStepsList).map(
          ([
            key,
            {
              type,
              title,
              content,
              ctaButton,
              onClick,
              ariaLabel,
              dataQa,
              dataQaId,
            },
          ]) => (
            <StepItem
              key={key}
              type={type}
              status={states[key]?.status || CompletionStatus.NOT_STARTED}
              title={title}
              content={content}
              ctaButton={ctaButton}
              onClick={onClick}
              ariaLabel={ariaLabel}
              dataQa={dataQa}
              dataQaId={dataQaId}
            />
          ),
        )}
      </div>
    </CollapsiblePanel>
  ) : null;
};

export default NextStepsPanel;
